/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



import {
  FormControl,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from "@mui/material";
import FormField from "components/generals/FormInput";
import FormikForm from "components/layout/FormikForm";
import { Form, FormikProps } from "formik";
import {
  HeaderEnum,
  TemplateContentTypeEnum,
  TemplateInterface,
  PrescriptionTypeEnum,
  TemplateTypeEnum,
} from "medicalsystem-shared";
import useHttp from "hooks/http.hook";
import { FormProps, TagsInterface, OptionColor } from "interfaces";
import BadgeIcon from "@mui/icons-material/Badge";
import { H4 } from "components/generals/_Titles";
import { EntityPermissions, FormActionType, PermissionsUser } from "types";
import { CoreAPI_URLs } from "constants/http.const";
import { Method } from "axios";
import { useTranslation } from "react-i18next";
import Button from "components/generals/Button";
import FormTextEditor from "components/generals/FormTextEditor";
import Box from "components/generals/Box";
import FormSelectInput from "components/generals/FormSelectInput";
import { templateTypeOptions } from "./custom/SelectOptionsList";
import FormActionButtons from "./custom/FormActionButtons";
import PermissionWrapper from "components/generals/PermissionWrapper";
import AutocompleteCheckboxes from "components/generals/AutocompleteCheckboxes";
import { useEffect, useState } from "react";
import { QueryResultInterface } from "medicalsystem-shared";
import { processTemplateTags, handleAddNewTag } from "utils/template.utils";

/**
 * HPI (History of Present Illness) and prescription template form component.
 * Manages template creation with rich text content, tags, and prescription type classification.
 * 
 * Features:
 * - Create/edit HPI and prescription templates
 * - Rich text editor for template content
 * - Template type selection (HPI, Prescription, etc.)
 * - Prescription type classification:
 *   - Medicine Prescription
 *   - Lab Order
 *   - Imaging Order
 * - Radio button selection for prescription types
 * - Tag-based template organization
 * - Multi-select tag assignment with autocomplete checkboxes
 * - Custom tag creation
 * - Automatic tag inheritance from filter context
 * - Color-coded tag visualization
 * - Template versioning (version 1.0)
 * - Content type: TEXT_HTML
 * - Permission-based access control
 * 
 * Template Types:
 * - HPI: History of Present Illness narratives
 * - Prescription: Medication orders, lab orders, imaging orders
 * 
 * Prescription Classification:
 * - Automatically adds prescription type tag
 * - Removes previous prescription type when changed
 * - Ensures only one prescription type tag per template
 * 
 * Tag Management:
 * - Select from existing tags
 * - Add new custom tags
 * - First tag is the custom/primary tag
 * - Additional tags inherited from filterTag prop
 * - Tags persist and can be reused across templates
 * 
 * Use Cases:
 * - Create standard HPI templates for common conditions
 * - Build prescription templates for frequently ordered medications
 * - Create lab order templates for routine tests
 * - Build imaging order templates for standard studies
 * - Standardize clinical documentation across providers
 * 
 * Security:
 * - Permission-based rendering via PermissionWrapper
 * - Validates entity permissions for edit/delete
 * - Uses HeaderEnum.RESOURCE_ID for secure identification
 * - HTML content sanitization in rich text editor
 * 
 * @param {FormProps<TemplateInterface>} props - Form properties
 * @param {TemplateInterface} props.model - Template data to edit or empty model for new
 * @param {FormActionType} props.action - Form action (NEW, MODIFY, READ_ONLY)
 * @param {Function} props.onCloseAction - Callback when form closes
 * @param {Function} props.setModelList - Callback to update template list
 * @param {TemplateInterface[]} props.modelList - Current list of templates
 * @param {TagsInterface[]} props.filterTag - Optional tags to filter/pre-select
 * 
 * @returns {JSX.Element} HPI/Prescription template form with rich text editor and type selection
 */
const HPITemplateForm: React.FC<FormProps<TemplateInterface>> = ({
  model,
  action,
  onCloseAction,
  readOnlyValue,
  setModelList,
  modelList,
  filterTag,
}) => {
  const [isLoading, _error, sendRequest] = useHttp();
  const [optionColors, setOptionColors] = useState<OptionColor[]>([]);
  const [AvailableTags, setAvailableTags] = useState<TagsInterface[]>([]);
  const [selectedTags, setSelectedTags] = useState<TagsInterface[]>([]);
  const { t } = useTranslation();
  const [newTag, setNewTag] = useState("");
  const [selectedPrescriptionType, setSelectedPrescriptionType] = useState<string | null>(null);
  const [showPrescriptionTypes, setShowPrescriptionTypes] = useState(false);
  const customTag = model.tags[0];
  filterTag = model.tags.map((tag) => ({ id: crypto.randomUUID(), name: tag }));

  const handlePrescriptionTypeChange = (type: string) => {
    setSelectedPrescriptionType(type);
    const updatedTags = [...(model.tags || [])].filter(
      (tag) =>
        tag !== PrescriptionTypeEnum.MEDICINE_PRESCRIPTION &&
        tag !== PrescriptionTypeEnum.LAB_ORDER &&
        tag !== PrescriptionTypeEnum.IMAGING_ORDER
    );
    updatedTags.push(type);
    return updatedTags;
  };

  const onGetTemplateHandler = (response: QueryResultInterface<TemplateInterface>) => {
    const { availableTags, selectedTags } = processTemplateTags(response, filterTag);
    setAvailableTags(availableTags);
    setSelectedTags(selectedTags);
  };

  const handleTagSelectionChange = (tags: TagsInterface[]) => {
    setSelectedTags(tags);
  };

  useEffect(() => {
    sendRequest(CoreAPI_URLs.HPI_TEMPLATE_GET_ALL, onGetTemplateHandler, "GET");
    if (filterTag && filterTag.length > 0) {
      setSelectedTags(filterTag);
    }
  }, []);

  const submitHandler = (model: Partial<TemplateInterface>) => {
    model.version = "1.0";
    model.contentType = TemplateContentTypeEnum.TEXT_HTML;
    if (
      customTag &&
      model.type == TemplateTypeEnum.PRESCRIPTION &&
      !model.tags.includes(customTag)
    ) {
      model.tags = [customTag, ...model.tags];
    }
    if (model.type == TemplateTypeEnum.PRESCRIPTION && action == FormActionType.NEW) {
      model.tags = [selectedPrescriptionType, ...model.tags];
    }
    const successMessageText = action === FormActionType.NEW ? t("templateForm.templateCreated") : t("templateForm.templateUpdated");
    const method: Method = action === FormActionType.NEW ? "POST" : "PUT";
    sendRequest(
      CoreAPI_URLs.HPI_TEMPLATE_GENERAL_OPERATIONS,
      onTemplateUpdateHandler,
      method,
      JSON.stringify(model),
      [{ header: HeaderEnum.RESOURCE_ID, value: model.id }],
      { successMessage: successMessageText }
    );
  };

  const handleAddTag = () => {
    const { updatedAvailableTags, updatedSelectedTags } = handleAddNewTag(
      newTag,
      AvailableTags,
      selectedTags
    );
    setAvailableTags(updatedAvailableTags);
    setSelectedTags(updatedSelectedTags);
    setNewTag("");
    return updatedSelectedTags;
  };

  const onTemplateUpdateHandler = (hpitemplate: TemplateInterface) => {
    if (action === FormActionType.MODIFY) {
      setModelList(modelList.map((model) => (model.id === hpitemplate.id ? hpitemplate : model)));
    } else {
      setModelList((oldArray) => [...oldArray, hpitemplate]);
    }
    onCloseAction();
  };

  const translatedTemplateTypeOptions = templateTypeOptions.map((option) => ({
    ...option,
    label: t(`templateForm.${option.value.toLowerCase()}`), 
  }));

  return (
    <FormikForm
      onSubmit={(model) => {
        submitHandler(model);
      }}
      initialValues={model}
    >
      {({ values, errors, touched, setFieldValue }: FormikProps<Partial<TemplateInterface>>) => (
        <Form id="hpitemplateForm" autoComplete="true">
          <Stack alignItems="center" direction="row" gap={2} mb={3}>
            <BadgeIcon fontSize="large" />
            <H4>{t("hpitemplateForm.hpitemplateData")}</H4>
          </Stack>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormField
                readOnly={action === FormActionType.NEW ? false : readOnlyValue}
                type="text"
                label={t("hpitemplateForm.title")}
                name="title"
                value={values.title || ""}
                error={errors.title && touched.title}
              />
            </Grid>
            <Grid item xs={12}>
              <FormTextEditor
                readOnly={action === FormActionType.NEW ? false : readOnlyValue}
                setFieldValue={(val) => setFieldValue("template", val)}
                value={values.template}
                editorHeight="30rem"
                wrapperHeight="40rem"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Grid container direction="row" spacing={2} mb={2} alignItems="center">
                <Grid item xs={9}>
                  <FormField
                    readOnly={action === FormActionType.NEW ? false : readOnlyValue}
                    type="text"
                    label={t("hpitemplateForm.tags")}
                    name="newTag"
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                  />
                </Grid>
                <Grid item>
                  {(action === FormActionType.NEW || !readOnlyValue) && (
                    <Button
                      name="addTagButton"
                      variant="contained"
                      color="success"
                      onClick={() => {
                        const updatedSelection = handleAddTag();
                        setFieldValue(
                          "tags",
                          updatedSelection.map((option) => option.name)
                        );
                      }}
                      size="small"
                    >
                      {t("hpitemplateForm.addTag")}
                    </Button>
                  )}
                </Grid>
              </Grid>
              
              {(action === FormActionType.NEW || !readOnlyValue) && (
               <Grid id="cy-tags">
                 <AutocompleteCheckboxes
                    options={AvailableTags}
                    label={t("hpitemplateForm.selectTags")}
                    placeholder={t("hpitemplateForm.chooseTags")}
                    onChange={(selected) => {
                      handleTagSelectionChange(selected);
                      setFieldValue(
                        "tags",
                        selected.map((option) => option.name)
                      );
                    }}
                    value={selectedTags}
                    optionColors={optionColors}
                    setOptionColors={setOptionColors}
                    getOptionLabel={(option) => option.name}
                  />
                </Grid>
              )}
            </Grid>
            <Grid item xs={6}>
              <Box>
                <FormSelectInput
                  id="cy-template-type"
                  readonly={action === FormActionType.MODIFY}
                  name="type"
                  label={t("hpitemplateForm.templateType")}
                  selectArray={translatedTemplateTypeOptions} 
                  onChange={(e) => {
                    const selectedType = e.target.value;
                    setFieldValue("type", selectedType);
                    if (selectedType === TemplateTypeEnum.PRESCRIPTION) {
                      setShowPrescriptionTypes(true);
                      setSelectedPrescriptionType(PrescriptionTypeEnum.MEDICINE_PRESCRIPTION);
                    } else {
                      setShowPrescriptionTypes(false);
                    }
                  }}
                  defaultValue={values.type}
                />
              </Box>
              {showPrescriptionTypes && action === FormActionType.NEW && (
                <>
                  <FormControl sx={{ mt: 1 }}>
                    <Typography variant="body1">{t("templateForm.prescriptionType")}</Typography>
                    <RadioGroup
                      row
                      name="prescriptionType"
                      value={selectedPrescriptionType}
                      onChange={(e) => handlePrescriptionTypeChange(e.target.value)}
                      defaultValue={PrescriptionTypeEnum.MEDICINE_PRESCRIPTION}
                    >
                      <FormControlLabel
                        value={PrescriptionTypeEnum.MEDICINE_PRESCRIPTION}
                        control={<Radio />}
                        label={t(`templateForm.${PrescriptionTypeEnum.MEDICINE_PRESCRIPTION.replace(/\s+/g, '')}`)}
                      />
                      <FormControlLabel
                        value={PrescriptionTypeEnum.LAB_ORDER}
                        control={<Radio />}
                        label={t(`templateForm.${PrescriptionTypeEnum.LAB_ORDER.replace(/\s+/g, '')}`)}
                      />
                      <FormControlLabel
                        value={PrescriptionTypeEnum.IMAGING_ORDER}
                        control={<Radio />}
                        label={t(`templateForm.${PrescriptionTypeEnum.IMAGING_ORDER.replace(/\s+/g, '')}`)}
                      />
                    </RadioGroup>
                  </FormControl>
                </>
              )}
            </Grid>
          </Grid>
          <Stack alignItems="center" direction="row" gap={1}>
            <div>
              {action === FormActionType.NEW ? (
                <PermissionWrapper
                  requiredPermissions={[PermissionsUser[EntityPermissions.HPI_PAGE].CREATE]}
                >
                  <Button type="submit" variant="contained" color="info" disabled={isLoading}>
                    {t("stepperFormActionButtons.submit")}
                  </Button>
                </PermissionWrapper>
              ) : (
                <PermissionWrapper
                  requiredPermissions={[PermissionsUser[EntityPermissions.HPI_PAGE].EDIT]}
                >
                  <Button type="submit" variant="contained" color="info" disabled={isLoading}>
                    {t("stepperFormActionButtons.update")}
                  </Button>
                </PermissionWrapper>
              )}
            </div>

            <FormActionButtons showSubmitButton={false} closeAction={onCloseAction} />
          </Stack>
        </Form>
      )}
    </FormikForm>
  );
};

export default HPITemplateForm;
